import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/content";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Images, Calendar } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const fullUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

const fmtDate = (d, lang) => {
  try { return new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};

function Lightbox({ photos, index, setIndex, onClose, lang }) {
  const prev = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + photos.length) % photos.length); }, [photos.length, setIndex]);
  const next = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % photos.length); }, [photos.length, setIndex]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);
  const p = photos[index];
  const title = lang === "en" && p.titolo_en ? p.titolo_en : p.titolo_it;
  return (
    <motion.div data-testid="gallery-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-[100] bg-foreground/95 backdrop-blur-sm flex flex-col items-center justify-center p-4">
      <button data-testid="gallery-lightbox-close" onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><X size={20} /></button>
      <motion.img key={index} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
        src={fullUrl(p.url)} alt={title} onClick={(e) => e.stopPropagation()} className="max-h-[76vh] max-w-[92vw] object-contain rounded-xl" />
      <p className="mt-4 text-white/80 font-serif-ed italic text-lg" onClick={(e) => e.stopPropagation()}>{title} · {index + 1}/{photos.length}</p>
      {photos.length > 1 && (
        <>
          <button data-testid="gallery-lightbox-prev" onClick={prev} className="absolute left-3 md:left-8 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronLeft size={22} /></button>
          <button data-testid="gallery-lightbox-next" onClick={next} className="absolute right-3 md:right-8 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronRight size={22} /></button>
        </>
      )}
    </motion.div>
  );
}

export default function Gallery() {
  const { t, lang, toggle } = useLang();
  const g = t.gallery;
  const [albums, setAlbums] = useState([]);
  const [box, setBox] = useState(null);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "auto";
    axios.get(`${API}/news`).then((r) => {
      const a = r.data
        .map((n) => ({
          id: n.id,
          titolo_it: n.titolo_it, titolo_en: n.titolo_en,
          data: n.data, categoria: n.categoria,
          images: (n.images && n.images.length ? n.images : (n.image_url ? [n.image_url] : [])),
        }))
        .filter((n) => n.images.length > 0);
      setAlbums(a);
    }).catch(() => setAlbums([]));
  }, []);

  const openAlbum = (album) => {
    const photos = album.images.map((u) => ({ url: u, titolo_it: album.titolo_it, titolo_en: album.titolo_en, categoria: album.categoria }));
    setBox({ photos, index: 0 });
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl border-b border-black/5">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="gallery-back" className="flex items-center gap-2.5">
            <img src={IMAGES.logo} alt="AGE Rende APS" className="h-10 w-10 rounded-full object-cover" />
            <span className="inline-flex items-center gap-2 text-sm font-semibold"><ArrowLeft size={16} /> {g.back}</span>
          </Link>
          <button onClick={toggle} className="text-xs font-semibold border border-foreground/20 rounded-full px-3 py-1.5 hover:bg-foreground hover:text-background transition-colors">
            {lang === "it" ? "EN" : "IT"}
          </button>
        </div>
      </header>

      <section className="pt-16 pb-16 md:pt-24 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-5">{g.tag}</span>
          <h1 className="font-display font-extrabold tracking-tighter text-5xl md:text-7xl leading-[0.95] mb-4">{g.title}</h1>
          <p className="text-muted-foreground text-lg max-w-xl mb-14">{g.subtitle}</p>

          {albums.length === 0 ? (
            <p className="text-muted-foreground text-lg">{g.empty}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map((album, i) => {
                const title = lang === "en" && album.titolo_en ? album.titolo_en : album.titolo_it;
                return (
                  <motion.button
                    key={album.id}
                    data-testid={`gallery-album-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    onClick={() => openAlbum(album)}
                    className="group text-left rounded-2xl overflow-hidden bg-surface border border-black/5 hover:border-primary/30 transition-all"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={fullUrl(album.images[0])} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors" />
                      <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wider bg-white/90 text-primary px-2.5 py-1 rounded-full">{album.categoria}</span>
                      <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-black/60 text-white px-2.5 py-1 rounded-full">
                        <Images size={13} /> {album.images.length} {g.photos}
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display font-bold text-xl tracking-tight mb-1.5 group-hover:text-primary transition-colors">{title}</h3>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Calendar size={13} /> {fmtDate(album.data, lang)}</span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {createPortal(
        <AnimatePresence>
          {box && (
            <Lightbox
              photos={box.photos}
              index={box.index}
              lang={lang}
              setIndex={(fn) => setBox((b) => ({ ...b, index: typeof fn === "function" ? fn(b.index) : fn }))}
              onClose={() => setBox(null)}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
